import React, { useEffect, useState, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Image,
  ScrollView,
  Dimensions } from 'react-native'
import { ArrowLeft, List, MapPin, Store, Navigation, X, ChevronRight } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

// Conditionally import Leaflet only for web
let MapContainer: any = null
let TileLayer: any = null
let Marker: any = null
let Popup: any = null
let useMap: any = null

if (Platform.OS === 'web') {
  try {
    const ReactLeaflet = require('react-leaflet')
    MapContainer = ReactLeaflet.MapContainer
    TileLayer = ReactLeaflet.TileLayer
    Marker = ReactLeaflet.Marker
    Popup = ReactLeaflet.Popup
    useMap = ReactLeaflet.useMap

    // Load leaflet CSS from CDN (local css url() imports not supported by Metro)
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css'
      document.head.appendChild(link)
    }

    // Fix default marker icons
    const L = require('leaflet')
    delete (L.Icon.Default.prototype as any)._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png' })

    // Inject CSS to fix Leaflet container height and style zoom controls
    const style = document.createElement('style')
    style.textContent = `
      .leaflet-container {
        width: 100% !important;
        height: 100% !important;
      }
      .leaflet-control-zoom a {
        color: #4A5D23 !important;
        font-weight: bold !important;
      }
      .leaflet-control-zoom a:hover {
        color: #3d4e1c !important;
      }
    `
    document.head.appendChild(style)
  } catch (e) {
    console.warn('Leaflet not available:', e)
  }
}

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MapBrowse'>
}

interface MarketLocation {
  id: string
  name: string
  store_type: string
  buyer_category: string | null
  specialty_type: string | null
  address_street: string | null
  address_city: string | null
  latitude: number | null
  longitude: number | null
  cover_image_url: string | null
}

// Helsinki center as default
const DEFAULT_CENTER: [number, number] = [60.1699, 24.9384]
const DEFAULT_ZOOM = 12

// Component to update map center when user location changes
function MapCenterUpdater({ center }: { center: [number, number] }) {
  const map = useMap?.()
  useEffect(() => {
    if (map && center) {
      map.setView(center, map.getZoom())
    }
  }, [map, center])
  return null
}

// Buyer-facing store categories
const STORE_CATEGORIES = {
  flea_market: {
    en: 'Flea Market',
    fi: 'Kirpputori',
    color: theme.colors.brand.primary },
  thrift: {
    en: 'Second-hand',
    fi: 'Käytetty tavara',
    color: '#4682B4' },
  vintage_antique: {
    en: 'Vintage & Antique',
    fi: 'Vintage & Antiikki',
    color: '#8B4513' },
  specialty: {
    en: 'Specialty',
    fi: 'Erikoisliikkeet',
    color: '#9370DB',
    subtypes: {
      records: { en: 'Records & Vinyl', fi: 'Levyt & Vinyyli' },
      books: { en: 'Books', fi: 'Kirjat' },
      sports: { en: 'Sports', fi: 'Urheilu' },
      electronics: { en: 'Electronics', fi: 'Elektroniikka' },
      toys: { en: 'Toys & Games', fi: 'Lelut & Pelit' },
      fashion: { en: 'Designer Fashion', fi: 'Merkkivaatteet' },
      art: { en: 'Art', fi: 'Taide' },
      furniture: { en: 'Furniture', fi: 'Huonekalut' } } } }

type StoreCategory = keyof typeof STORE_CATEGORIES
type SpecialtySubtype = keyof typeof STORE_CATEGORIES.specialty.subtypes

export default function MapBrowseScreen({ navigation }: Props) {
  const { t, language } = useLanguage()
  const [loading, setLoading] = useState(true)
  const [markets, setMarkets] = useState<MarketLocation[]>([])
  const [selectedMarket, setSelectedMarket] = useState<MarketLocation | null>(null)
  const [showSpecialtyMenu, setShowSpecialtyMenu] = useState(false)
  const [filterType, setFilterType] = useState<string | null>(null)
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null)
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER)

  useEffect(() => {
    fetchMarkets()
    requestUserLocation()
  }, [])

  const requestUserLocation = () => {
    if (Platform.OS === 'web' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          setUserLocation([latitude, longitude])
          setMapCenter([latitude, longitude])
        },
        (error) => {
          console.log('Location access denied or unavailable:', error.message)
          // Keep default Helsinki center
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      )
    }
  }

  // Calculate distance between two coordinates in km (Haversine formula)
  const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371 // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLon = (lon2 - lon1) * Math.PI / 180
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    return R * c
  }

  // Map store_type to buyer-facing category (temporary until DB has buyer_category field)
  const getBuyerCategory = (storeType: string): { category: string; specialty: string | null } => {
    const mapping: Record<string, { category: string; specialty: string | null }> = {
      self_service_flea_market: { category: 'flea_market', specialty: null },
      full_service_flea_market: { category: 'flea_market', specialty: null },
      consignment_store: { category: 'thrift', specialty: null },
      retail_store: { category: 'thrift', specialty: null },
      // Legacy types
      flea_market: { category: 'flea_market', specialty: null },
      vintage_store: { category: 'vintage_antique', specialty: null },
      antique_shop: { category: 'vintage_antique', specialty: null },
      thrift_store: { category: 'thrift', specialty: null } }
    return mapping[storeType] || { category: 'thrift', specialty: null }
  }

  // Geocode a single address via Digitransit, fallback to Nominatim
  const geocodeAddress = async (street: string | null, city: string | null): Promise<{ lat: number; lng: number } | null> => {
    const fullAddress = [street, city, 'Finland'].filter(Boolean).join(', ')
    if (!fullAddress || fullAddress === 'Finland') return null

    // Check localStorage cache
    const cacheKey = `geocode_${fullAddress}`
    if (Platform.OS === 'web') {
      try {
        const cached = localStorage.getItem(cacheKey)
        if (cached) return JSON.parse(cached)
      } catch {}
    }

    // Try Digitransit (Finnish national address DB)
    try {
      const res = await fetch(
        `https://api.digitransit.fi/geocoding/v1/search?text=${encodeURIComponent(fullAddress)}&size=1&lang=fi`
      )
      if (res.ok) {
        const data = await res.json()
        const coords = data?.features?.[0]?.geometry?.coordinates
        if (coords) {
          const result = { lat: coords[1], lng: coords[0] }
          if (Platform.OS === 'web') localStorage.setItem(cacheKey, JSON.stringify(result))
          return result
        }
      }
    } catch {}

    // Fallback: Nominatim
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`,
        { headers: { 'User-Agent': 'Fliisi-App/1.0' } }
      )
      if (res.ok) {
        const data = await res.json()
        if (data?.[0]) {
          const result = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }
          if (Platform.OS === 'web') localStorage.setItem(cacheKey, JSON.stringify(result))
          return result
        }
      }
    } catch {}

    return null
  }

  const fetchMarkets = async () => {
    setLoading(true)
    try {
      // Try the store_locations view first (has pre-extracted lat/lng from PostGIS)
      let storeData: any[] | null = null

      try {
        const { data, error } = await supabase
          .from('store_locations')
          .select('id, name, store_type, address_street, address_city, cover_image_url, latitude, longitude')
        if (!error && data) storeData = data
      } catch {}

      // Fallback: query flea_markets directly
      if (!storeData) {
        const { data, error } = await supabase
          .from('flea_markets')
          .select('id, name, store_type, address_street, address_city, cover_image_url')
          .eq('status', 'active')
        if (error) throw error
        storeData = (data || []).map(m => ({ ...m, latitude: null, longitude: null }))
      }

      // Map categories
      const mapped = (storeData || []).map((market) => {
        const { category, specialty } = getBuyerCategory(market.store_type)
        return { ...market, buyer_category: category, specialty_type: specialty }
      })

      // Split into stores with/without coordinates
      const withCoords = mapped.filter(m => m.latitude != null && m.longitude != null)
      const needsGeocoding = mapped.filter(m =>
        m.latitude == null && m.longitude == null &&
        (m.address_street || m.address_city)
      )

      // Show stores that already have coordinates immediately
      if (withCoords.length > 0) {
        setMarkets(withCoords)
        setLoading(false)
      }

      // Geocode stores missing coordinates in the background
      if (needsGeocoding.length > 0) {
        const geocoded: MarketLocation[] = []
        for (const market of needsGeocoding) {
          const coords = await geocodeAddress(market.address_street, market.address_city)
          if (coords) {
            geocoded.push({ ...market, latitude: coords.lat, longitude: coords.lng })
          }
        }
        if (geocoded.length > 0) {
          setMarkets(prev => [...prev, ...geocoded])
        }
      }
    } catch (error) {
      console.error('Error fetching markets:', error)
    } finally {
      setLoading(false)
    }
  }

  const getCategoryLabel = (category: string) => {
    const cat = STORE_CATEGORIES[category as StoreCategory]
    if (cat) {
      return language === 'fi' ? cat.fi : cat.en
    }
    return category
  }

  const getSpecialtyLabel = (specialty: string) => {
    const sub = STORE_CATEGORIES.specialty.subtypes[specialty as SpecialtySubtype]
    if (sub) {
      return language === 'fi' ? sub.fi : sub.en
    }
    return specialty
  }

  // Filter and sort markets by distance from user
  const filteredMarkets = (() => {
    let result = filterType
      ? filterType.startsWith('specialty:')
        ? markets.filter(m => m.specialty_type === filterType.replace('specialty:', ''))
        : markets.filter(m => m.buyer_category === filterType)
      : markets

    // Sort by distance if user location is available
    if (userLocation) {
      result = [...result].sort((a, b) => {
        if (!a.latitude || !a.longitude) return 1
        if (!b.latitude || !b.longitude) return -1
        const distA = getDistance(userLocation[0], userLocation[1], a.latitude, a.longitude)
        const distB = getDistance(userLocation[0], userLocation[1], b.latitude, b.longitude)
        return distA - distB
      })
    }
    return result
  })()

  const handleMarkerClick = (market: MarketLocation) => {
    setSelectedMarket(market)
  }

  const handleViewStore = () => {
    if (selectedMarket) {
      navigation.navigate('StoreBrowse', {
        marketId: selectedMarket.id,
        marketName: selectedMarket.name })
    }
  }

  // Get distance text for display
  const getDistanceText = (market: MarketLocation): string => {
    if (!userLocation || !market.latitude || !market.longitude) return ''
    const dist = getDistance(userLocation[0], userLocation[1], market.latitude, market.longitude)
    if (dist < 1) {
      return `${Math.round(dist * 1000)} m`
    }
    return `${dist.toFixed(1)} km`
  }

  // Get 5 closest markets
  const closestMarkets = filteredMarkets.slice(0, 5)

  const renderClosestList = () => {
    if (!userLocation || closestMarkets.length === 0) return null

    return (
      <View style={styles.closestListContainer}>
        <Text style={styles.closestListTitle}>
          {language === 'fi' ? 'Lähimmät' : 'Nearest'}
        </Text>
        {closestMarkets.map((market, index) => {
          const cat = STORE_CATEGORIES[market.buyer_category as StoreCategory]
          return (
            <TouchableOpacity
              key={market.id}
              style={styles.closestListItem}
              onPress={() => {
                setSelectedMarket(market)
                if (market.latitude && market.longitude) {
                  setMapCenter([market.latitude, market.longitude])
                }
              }}
            >
              <View style={[styles.closestListDot, { backgroundColor: cat?.color || theme.colors.brand.primary }]} />
              <View style={styles.closestListInfo}>
                <Text style={styles.closestListName} numberOfLines={1}>{market.name}</Text>
                <Text style={styles.closestListCategory}>{getCategoryLabel(market.buyer_category || 'thrift')}</Text>
              </View>
              <Text style={styles.closestListDistance}>{getDistanceText(market)}</Text>
            </TouchableOpacity>
          )
        })}
      </View>
    )
  }

  const renderFilterChips = () => {
    const categories: StoreCategory[] = ['flea_market', 'thrift', 'vintage_antique', 'specialty']

    return (
      <View style={styles.filterWrapper}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filterContainer}
        >
          <TouchableOpacity
            style={[styles.filterChip, !filterType && styles.filterChipActive]}
            onPress={() => {
              setFilterType(null)
              setShowSpecialtyMenu(false)
            }}
          >
            <Text style={[styles.filterChipText, !filterType && styles.filterChipTextActive]}>
              {t('all') || 'Kaikki'}
            </Text>
          </TouchableOpacity>
          {categories.map(category => {
            const cat = STORE_CATEGORIES[category]
            const isSpecialty = category === 'specialty'
            const isActive = isSpecialty
              ? (filterType === 'specialty' || filterType?.startsWith('specialty:'))
              : filterType === category

            return (
              <TouchableOpacity
                key={category}
                style={[
                  styles.filterChip,
                  isActive && styles.filterChipActive,
                  { borderColor: cat.color }
                ]}
                onPress={() => {
                  if (isSpecialty) {
                    setShowSpecialtyMenu(!showSpecialtyMenu)
                    if (!showSpecialtyMenu && !filterType?.startsWith('specialty:')) {
                      setFilterType('specialty')
                    }
                  } else {
                    setFilterType(filterType === category ? null : category)
                    setShowSpecialtyMenu(false)
                  }
                }}
              >
                <View
                  style={[styles.filterDot, { backgroundColor: cat.color }]}
                />
                <Text style={[
                  styles.filterChipText,
                  isActive && styles.filterChipTextActive
                ]}>
                  {getCategoryLabel(category)}
                </Text>
              </TouchableOpacity>
            )
          })}
        </ScrollView>

        {/* Specialty submenu */}
        {showSpecialtyMenu && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.filterContainer}
            style={styles.specialtyMenu}
          >
            {Object.keys(STORE_CATEGORIES.specialty.subtypes).map(subtype => {
              const count = markets.filter(m => m.specialty_type === subtype).length
              const isActive = filterType === `specialty:${subtype}`
              return (
                <TouchableOpacity
                  key={subtype}
                  style={[
                    styles.filterChip,
                    styles.filterChipSmall,
                    isActive && styles.filterChipActive,
                  ]}
                  onPress={() => setFilterType(isActive ? 'specialty' : `specialty:${subtype}`)}
                >
                  <Text style={[
                    styles.filterChipText,
                    styles.filterChipTextSmall,
                    isActive && styles.filterChipTextActive
                  ]}>
                    {getSpecialtyLabel(subtype)}
                  </Text>
                </TouchableOpacity>
              )
            })}
          </ScrollView>
        )}
      </View>
    )
  }

  const renderStorePreview = () => {
    if (!selectedMarket) return null

    return (
      <View style={styles.previewSheet}>
        <TouchableOpacity
          style={styles.previewCloseButton}
          onPress={() => setSelectedMarket(null)}
        >
          <X size={20} color="#666" />
        </TouchableOpacity>

        <View style={styles.previewContent}>
          {selectedMarket.cover_image_url ? (
            <Image
              source={{ uri: selectedMarket.cover_image_url }}
              style={styles.previewImage}
            />
          ) : (
            <View style={styles.previewImagePlaceholder}>
              <Store size={32} color={theme.colors.text.secondary} />
            </View>
          )}

          <View style={styles.previewInfo}>
            <Text style={styles.previewName}>{selectedMarket.name}</Text>
            <View style={styles.previewTypeRow}>
              <View
                style={[
                  styles.previewTypeBadge,
                  { backgroundColor: STORE_CATEGORIES[selectedMarket.buyer_category as StoreCategory]?.color || theme.colors.brand.primary }
                ]}
              >
                <Text style={styles.previewTypeText}>
                  {getCategoryLabel(selectedMarket.buyer_category || 'thrift')}
                </Text>
              </View>
            </View>
            {(selectedMarket.address_street || selectedMarket.address_city) && (
              <View style={styles.previewAddressRow}>
                <MapPin size={14} color="#666" />
                <Text style={styles.previewAddress}>
                  {[selectedMarket.address_street, selectedMarket.address_city]
                    .filter(Boolean)
                    .join(', ')}
                </Text>
              </View>
            )}
            {userLocation && selectedMarket.latitude && selectedMarket.longitude && (
              <Text style={styles.previewDistance}>
                {getDistanceText(selectedMarket)}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.previewButtons}>
          {/* Buyer action — always visible */}
          <TouchableOpacity style={styles.viewStoreButton} onPress={handleViewStore}>
            <Text style={styles.viewStoreButtonText}>
              {language === 'fi' ? 'Selaa tuotteita' : 'Browse Products'}
            </Text>
          </TouchableOpacity>

          {/* Seller action — depends on store type */}
          <TouchableOpacity
            style={styles.sellButton}
            onPress={() => {
              const isFleaMarket = ['flea_market', 'self_service_flea_market', 'full_service_flea_market'].includes(selectedMarket.store_type)
              if (isFleaMarket) {
                navigation.navigate('SpaceBrowse', { marketId: selectedMarket.id })
              } else {
                navigation.navigate('SubmitOffer', { storeId: selectedMarket.id })
              }
            }}
          >
            <Text style={styles.sellButtonText}>
              {(() => {
                const isFleaMarket = ['flea_market', 'self_service_flea_market', 'full_service_flea_market'].includes(selectedMarket.store_type)
                if (language === 'fi') return isFleaMarket ? 'Varaa paikka' : 'Tarjoa tuotteita'
                return isFleaMarket ? 'Reserve Space' : 'Offer Products'
              })()}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    )
  }

  const renderMap = () => {
    if (Platform.OS !== 'web' || !MapContainer) {
      return (
        <View style={styles.noMapContainer}>
          <MapPin size={48} color={theme.colors.text.secondary} />
          <Text style={styles.noMapText}>
            {t('map_not_available') || 'Map view is only available on web'}
          </Text>
          <TouchableOpacity
            style={styles.listViewButton}
            onPress={() => navigation.navigate('BuyerHome')}
          >
            <List size={20} color="#fff" />
            <Text style={styles.listViewButtonText}>
              {t('switch_to_list') || 'Switch to List View'}
            </Text>
          </TouchableOpacity>
        </View>
      )
    }

    const L = require('leaflet')

    // Create custom icon for user location
    const userIcon = L.divIcon({
      className: 'user-location-marker',
      html: '<div style="width: 16px; height: 16px; background: #4285F4; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8] })

    // Create colored icons for each store type
    const createStoreIcon = (color: string) => L.divIcon({
      className: 'store-marker',
      html: `<div style="width: 28px; height: 28px; background: ${color}; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      </div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14] })

    return (
      <View style={styles.mapWrapper}>
        <MapContainer
          center={mapCenter}
          zoom={DEFAULT_ZOOM}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
        >
          <MapCenterUpdater center={mapCenter} />
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          {/* User location marker */}
          {userLocation && (
            <Marker position={userLocation} icon={userIcon}>
              <Popup>{t('your_location') || 'Your location'}</Popup>
            </Marker>
          )}
          {filteredMarkets.map(market => {
            if (!market.latitude || !market.longitude) return null
            const cat = STORE_CATEGORIES[market.buyer_category as StoreCategory]
            const markerColor = cat?.color || theme.colors.brand.primary
            return (
              <Marker
                key={market.id}
                position={[market.latitude, market.longitude]}
                icon={createStoreIcon(markerColor)}
                eventHandlers={{
                  click: () => handleMarkerClick(market) }}
              />
            )
          })}
        </MapContainer>
        {/* Center on my location button */}
        <TouchableOpacity
          style={styles.myLocationButton}
          onPress={() => {
            if (userLocation) {
              setMapCenter(userLocation)
            } else {
              requestUserLocation()
            }
          }}
        >
          <Navigation size={20} color={userLocation ? theme.colors.brand.primary : theme.colors.text.secondary} />
        </TouchableOpacity>
      </View>
    )
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <ArrowLeft size={24} color={theme.colors.brand.primary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('map_view') || 'Map View'}</Text>
          <TouchableOpacity
            style={styles.listButton}
            onPress={() => navigation.navigate('BuyerHome')}
          >
            <List size={24} color={theme.colors.brand.primary} />
          </TouchableOpacity>
        </View>
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <ArrowLeft size={24} color={theme.colors.brand.primary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('map_view') || 'Map View'}</Text>
        <TouchableOpacity
          style={styles.listButton}
          onPress={() => navigation.navigate('BuyerHome')}
        >
          <List size={24} color={theme.colors.brand.primary} />
        </TouchableOpacity>
      </View>

      {/* Filter Chips */}
      {renderFilterChips()}

      {/* Map - takes remaining space */}
      <View style={styles.mapContainer}>
        {renderMap()}
      </View>

      {/* Store Preview Sheet */}
      {renderStorePreview()}
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  backButton: {
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center' },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  listButton: {
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center' },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  filterWrapper: {
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8 },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: theme.colors.background.primary,
    borderWidth: 1,
    borderColor: theme.colors.ui.border,
    gap: 6 },
  filterChipActive: {
    backgroundColor: theme.colors.brand.primary,
    borderColor: theme.colors.brand.primary },
  filterChipText: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  filterChipTextActive: {
    color: '#fff' },
  filterChipSmall: {
    paddingHorizontal: 10,
    paddingVertical: 4 },
  filterChipTextSmall: {
    fontSize: 12 },
  filterDot: {
    width: 8,
    height: 8,
    borderRadius: 4 },
  specialtyMenu: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border,
    backgroundColor: theme.colors.background.secondary },
  closestListContainer: {
    backgroundColor: theme.colors.background.card,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border,
    maxHeight: 200 },
  closestListTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.secondary,
    marginBottom: 8 },
  closestListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 10 },
  closestListDot: {
    width: 10,
    height: 10,
    borderRadius: 5 },
  closestListInfo: {
    flex: 1 },
  closestListName: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  closestListCategory: {
    fontSize: 12,
    color: theme.colors.text.secondary },
  closestListDistance: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.brand.primary },
  mapContainer: {
    flex: 1 },
  mapWrapper: {
    flex: 1,
    overflow: 'hidden',
    position: 'relative' },
  myLocationButton: {
    position: 'absolute',
    top: 80,
    left: 10,
    width: 34,
    height: 34,
    borderRadius: 4,
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.2)',
    zIndex: 1000 },
  noMapContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32 },
  noMapText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 24 },
  listViewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.brand.primary,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8 },
  listViewButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  previewSheet: {
    position: 'absolute',
    bottom: 80,
    left: 16,
    right: 16,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    ...theme.shadows.medium },
  previewCloseButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1 },
  previewContent: {
    flexDirection: 'row',
    gap: 12 },
  previewImage: {
    width: 80,
    height: 80,
    borderRadius: 8 },
  previewImagePlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center' },
  previewInfo: {
    flex: 1,
    paddingRight: 24 },
  previewName: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  previewTypeRow: {
    flexDirection: 'row',
    marginBottom: 6 },
  previewTypeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4 },
  previewTypeText: {
    fontSize: 11,
    color: '#fff',
    fontWeight: '600' },
  previewAddressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4 },
  previewAddress: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    flex: 1 },
  previewProducts: {
    fontSize: 13,
    color: theme.colors.brand.primary,
    fontWeight: '500' },
  previewDistance: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.brand.primary,
    marginTop: 4 },
  previewButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12 },
  viewStoreButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.brand.primary,
    paddingVertical: 12,
    borderRadius: 10 },
  viewStoreButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600' },
  sellButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background.card,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: theme.colors.brand.secondary },
  sellButtonText: {
    color: theme.colors.brand.secondary,
    fontSize: 14,
    fontWeight: '600' } })
